import { useEffect, useRef, useState } from 'react';

import { Close, Search } from '@agendapro/emerald-icons';
import { useTranslation } from 'react-i18next';
import { SearchBarContainer } from './SearchBar.styles';
import { SearchBarProps } from './types';
import { Paragraph } from '@/UI';
import { Icon } from '../..';

const SearchBar = <T,>({
  onChange,
  placeholder,
  className,
  value,
  totalFilteredLocations,
  totalLocations,
  expandable,
}: SearchBarProps<T>) => {
  const { t } = useTranslation();
  const [showSearchResult, setShowSearchResult] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [showInput, setShowInput] = useState(false);

  useEffect(() => {
    setShowSearchResult(totalFilteredLocations! > 0 && value?.trim() !== '');
  }, [value, totalFilteredLocations]);

  return (
    <SearchBarContainer className={className} data-testid="searchBar" expandable={expandable}>
      {expandable ? (
        <div
          className={`cursor-pointer border border-solid border-border-primaryInverse rounded-[12px] box-border overflow-hidden flex items-center duration-500 ease-in right-0 p-2 h-[42px] max-h-[42px] [&_svg]:[-webkit-transform:translate(0px,0px)] ${
            showInput ? 'w-[248px] px-2' : 'w-[42px]'
          }`}
        >
          <button
            onClick={() => {
              if (!showInput) {
                inputRef?.current?.focus();
              }
              setShowInput((last) => !last);
            }}
            onKeyDown={() => {
              if (!showInput) {
                inputRef?.current?.focus();
              }
              setShowInput((last) => !last);
            }}
            type="button"
            className="m-0 p-0 box-border h-full"
            data-testid="expandable-search-bar"
          >
            <Search size={24} />
          </button>
          <input
            className={`bg-transparent border-0 text-text-default-inverse box-border h-[22px] max-h-[22px] outline-none duration-500 ease-in ml-1 placeholder-shown:text-ellipsis placeholder:text-text-default-inverse placeholder:text-ellipsis placeholder:text-sm placeholder:font-body ${
              showInput ? 'w-[190px]' : 'w-0'
            }`}
            ref={inputRef}
            placeholder={placeholder || ''}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            type="text"
            data-testid="expandable-input"
          />
          <button
            type="button"
            onClick={() => {
              onChange('');
              setShowInput((last) => !last);
            }}
            className="m-0 p-0 box-border h-full"
            data-testid="expandable-close"
          >
            <Close size={12} className={`ml-[2px] duration-500 ease-in ${showInput ? 'w-3' : 'w-0'}`} />
          </button>
        </div>
      ) : (
        <div className="searchInputContainer">
          <Icon icon={<Search />} size="small" />
          <input
            value={value || ''}
            className="searchInput"
            type="text"
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      )}
      {showSearchResult && (
        <Paragraph className="searchResultText" data-testid="searchResult">
          {t('SEARCH_RESULT', {
            totalFilteredLocations,
            totalLocations,
          })}
        </Paragraph>
      )}
    </SearchBarContainer>
  );
};

export default SearchBar;
